import { useEffect, useState } from "react";
import { api, BRL } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Contact2, TrendingUp, AlertTriangle, Activity, DollarSign, Factory, ChevronDown, ChevronUp, Truck, Clock, CheckCircle2, Loader2, FileBarChart } from "lucide-react";

const STAGE_LABEL = {
  novo_lead: "Novo Lead", contato: "Contato", orcamento: "Orçamento",
  negociacao: "Negociação", fechado: "Fechado", perdido: "Perdido",
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [prod, setProd] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [running, setRunning] = useState(false);

  const load = () => {
    api.get("/dashboard/summary").then(({ data }) => setData(data));
    api.get("/dashboard/production").then(({ data }) => setProd(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const runReport = async () => {
    if (!window.confirm("Gerar o relatório da semana agora? Isso arquiva os cards Fechado/Perdido do funil e envia o resumo no seu WhatsApp.")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/reports/weekly/run", { send_whatsapp: true });
      toast.success(`Relatório gerado. ${data.archived} card(s) arquivados.${data.wa_sent ? " Enviado no WhatsApp." : (data.wa_error ? " WhatsApp: " + data.wa_error : "")}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar relatório");
    } finally { setRunning(false); }
  };

  if (!data) return <div className="p-8 text-slate-500">Carregando...</div>;

  const kpis = [
    { label: "Novos leads", value: data.new_leads, icon: Contact2, tid: "kpi-leads" },
    { label: "Oportunidades abertas", value: (data.stages.novo_lead || 0) + (data.stages.contato || 0) + (data.stages.orcamento || 0) + (data.stages.negociacao || 0), icon: TrendingUp, tid: "kpi-open" },
    { label: "Valor pipeline", value: BRL(data.opportunities_total_value), icon: DollarSign, tid: "kpi-value" },
    { label: "Tarefas atrasadas", value: data.overdue_tasks, icon: AlertTriangle, tid: "kpi-overdue" },
  ];

  const prodCards = prod ? [
    { label: "Valor em produção", value: BRL(prod.value_in_production || 0), icon: DollarSign, cls: "text-emerald-400", tid: "prod-kpi-value" },
    { label: "Em produção", value: prod.in_production, icon: Factory, cls: "text-[#0CDBF2]", tid: "prod-kpi-inprod" },
    { label: "Entregas esta semana", value: prod.due_this_week, icon: Truck, cls: "text-emerald-400", tid: "prod-kpi-week" },
    { label: "Entregas próxima semana", value: prod.due_next_week, icon: Clock, cls: "text-sky-400", tid: "prod-kpi-next" },
    { label: "Atrasados", value: prod.overdue, icon: AlertTriangle, cls: "text-red-400", tid: "prod-kpi-overdue" },
    { label: "Entregues na semana", value: prod.delivered_week, icon: CheckCircle2, cls: "text-green-400", tid: "prod-kpi-delivered" },
  ] : [];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-slate-400 text-sm mt-1">Visão geral do seu funil comercial</p>
        </div>
        <Button onClick={runReport} disabled={running} variant="outline"
          className="border-[#0CDBF2]/40 text-[#0CDBF2] hover:bg-[#0CDBF2]/10" data-testid="dash-run-report">
          {running ? <Loader2 size={14} className="animate-spin mr-2" /> : <FileBarChart size={14} className="mr-2" />}
          Gerar relatório da semana
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <Card key={k.label} data-testid={k.tid}
            className="bg-[#111622] border-[#1E293B] p-5 hover:border-[#0CDBF2]/40 transition-colors">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">{k.label}</div>
                <div className="font-heading text-2xl font-bold mt-2">{k.value}</div>
              </div>
              <k.icon className="text-[#0CDBF2]" size={20} />
            </div>
          </Card>
        ))}
      </div>

      {/* Produção */}
      <Card className="bg-[#111622] border-[#1E293B] p-6" data-testid="dash-production">
        <div className="flex items-center gap-2 mb-4">
          <Factory className="text-[#0CDBF2]" size={16} />
          <div className="font-heading font-semibold">Produção</div>
          <button onClick={() => setShowDetails((v) => !v)} data-testid="prod-details-toggle"
            className="ml-auto flex items-center gap-1 text-xs text-slate-400 hover:text-[#0CDBF2] transition-colors">
            {showDetails ? <>Ocultar detalhes <ChevronUp size={14} /></> : <>Ver detalhes por etapa <ChevronDown size={14} /></>}
          </button>
        </div>
        {!prod ? (
          <div className="text-slate-500 text-sm">Carregando produção...</div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {prodCards.map((p) => (
                <div key={p.label} data-testid={p.tid} className="rounded-lg border border-[#1E293B] bg-[#0B0F17] p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono leading-tight">{p.label}</div>
                    <p.icon className={p.cls} size={16} />
                  </div>
                  <div className={`font-heading text-2xl font-bold mt-2 ${p.cls}`}>{p.value}</div>
                </div>
              ))}
            </div>

            {showDetails && (
              <div className="mt-6 space-y-2" data-testid="prod-stage-breakdown">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-2">Itens por etapa</div>
                {(prod.stages || []).map((s) => {
                  const val = prod.by_stage?.[s.key] || 0;
                  const total = Object.values(prod.by_stage || {}).reduce((a, b) => a + b, 0) || 1;
                  const pct = (val / total) * 100;
                  return (
                    <div key={s.key} data-testid={`prod-stage-row-${s.key}`}>
                      <div className="flex justify-between text-xs text-slate-400 mb-1">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />{s.name}
                        </span>
                        <span>{val} · <span className="text-emerald-400">{BRL(prod.value_by_stage?.[s.key] || 0)}</span></span>
                      </div>
                      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                        <div className="h-full" style={{ width: `${pct}%`, background: s.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="bg-[#111622] border-[#1E293B] p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="text-[#0CDBF2]" size={16} />
            <div className="font-heading font-semibold">Distribuição do funil</div>
          </div>
          <div className="space-y-2">
            {Object.keys(STAGE_LABEL).map((k) => {
              const val = data.stages[k] || 0;
              const total = Object.values(data.stages).reduce((a, b) => a + b, 0) || 1;
              const pct = (val / total) * 100;
              return (
                <div key={k} data-testid={`stage-row-${k}`}>
                  <div className="flex justify-between text-xs text-slate-400 mb-1"><span>{STAGE_LABEL[k]}</span><span>{val}</span></div>
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-[#0CDBF2]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="bg-[#111622] border-[#1E293B] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="text-[#0CDBF2]" size={16} />
            <div className="font-heading font-semibold">Atividade recente</div>
          </div>
          <div className="space-y-3">
            {data.activities?.length ? data.activities.map((a) => (
              <div key={a.id} className="text-sm flex gap-3 border-b border-slate-800/70 pb-3 last:border-0" data-testid={`activity-${a.id}`}>
                <div className="w-1.5 h-1.5 rounded-full bg-[#0CDBF2] mt-2" />
                <div>
                  <div className="text-slate-200">
                    <span className="font-semibold">{a.user_name || "Sistema"}</span>{" "}
                    {a.action === "quote_request" ? `— nova solicitação de ${a.meta?.customer}${a.meta?.company ? " (" + a.meta.company + ")" : ""}` : `— ${a.action}`}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{new Date(a.created_at).toLocaleString("pt-BR")}</div>
                </div>
              </div>
            )) : <div className="text-slate-500 text-sm">Sem atividade recente.</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}
